import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

/**
 * Screen 1g — More. The phone's overflow destination.
 *
 * WHY IT EXISTS. `MOBILE_NAV` carries four items and `DESKTOP_NAV` seven. Two of
 * the difference — Trips and Zones — are shipped screens the phone could not
 * reach properly, and `/zones` could not be reached AT ALL: it had no in-app link
 * anywhere, and the comment that justified that said the screen "stays reachable
 * by URL on any viewport". `app.raadtelematics.com` ships `display: standalone`,
 * which REMOVES THE ADDRESS BAR, so in the product's own installed form there was
 * no URL to type. A page built and hidden from the people it is for.
 *
 * TWO ROWS, AND THAT IS THE POINT — it is not a two-item menu, it is a five-item
 * screen built one fifth of. Canvas 1g draws Geofences, Reports, Users & roles,
 * Notification channels and support; only Zones exists today, and Trips joins it
 * because Mahad asked for a path that is not the "Distance today" tile. The rest
 * arrive as they ship. NOTHING is listed here that does not exist — no disabled
 * row, no "coming soon". CLAUDE.md: absence over disabled.
 *
 * ⚠️ PROVENANCE. The shape is taken from canvas 1g, but every mobile artboard is
 * labelled `Consumed by: — (Mobile app … — Phase 4 (Flutter))`. It was drawn for
 * the Flutter app, not this PWA. We took the SHAPE, NOT A SPEC — do not cite 1g
 * as binding on this app in either direction.
 *
 * NO MUTE. Canvas 2d shows a mute control on the mobile zones screen. Mute does
 * not exist anywhere in `web/` or `api/`, so it is not here either.
 *
 * PADDING LIVES ON THE ROUTE, NOT THE SHELL — and getting that backwards is
 * how this shipped edge-to-edge. web/AppShell.tsx renders
 * `<main className="flex flex-1 flex-col pb-20 lg:pb-0">` with NO horizontal
 * padding, so every web route supplies its own (Settings: `px-4 pb-24 pt-4
 * sm:px-6`). The ADMIN shell is the opposite — its <main> pads and its routes
 * do not — and this file was written straight after the admin rail, carrying
 * the admin habit across. Bottom spacing still comes from <main>'s `pb-20`,
 * which clears the fixed tab bar.
 *
 * Eagerly imported by the router rather than lazy: a static link list has nothing
 * to load, so a `RouteFallback` skeleton would flash for no reason, and it pulls
 * no maplibre, so the entry chunk stays clean.
 */

const ROWS = [
  { to: '/zones', key: 'zones' },
  { to: '/trips', key: 'trips' },
  // Slice 24. The tab bar is full at five, so this row is the ONLY in-app path
  // to /places on a phone — and the installed PWA ships `display: standalone`,
  // which removes the address bar, so "reachable by URL" is not reachability
  // here. That exact reasoning hid /zones from the people it was for for three
  // slices. Places sits next to Zones for the same reason it does in the desktop
  // rail: they are neighbouring ideas and must not be confused for each other.
  { to: '/places', key: 'places' },
  { to: '/reports', key: 'reports' },
  // Slice 28. The phone is the real target — a fleet manager on an Android
  // phone — and the tab bar is full at four, so this is the ONLY in-app path to
  // /maintenance on a phone. The installed PWA ships `display: standalone` and
  // has no address bar, so "reachable by URL" is not reachability here; that
  // exact reasoning hid /zones from the people it was for, for three slices.
  { to: '/maintenance', key: 'maintenance' },
  // Slice 29. Same reasoning as /maintenance above: the tab bar is full at four,
  // so this row is the ONLY in-app path to /behaviour on a phone, and the
  // installed PWA has no address bar to type one into. The entry lands WITH the
  // screen — never before it.
  { to: '/behaviour', key: 'behaviour' },
  // Slice 22. Same reasoning again, and it bites harder here than anywhere: a
  // share link is created and REVOKED from a phone, in the minute the customer
  // realises it went to the wrong person. With the tab bar full at four, this
  // row is the only in-app path to /shares on the device where that happens.
  { to: '/shares', key: 'shares' },
] as const;

const CHEVRON = 'M9 18l6-6-6-6';

export function More() {
  const { t } = useTranslation();

  return (
    <section className="mx-auto w-full max-w-2xl px-4 pt-4 sm:px-6">
      <h1 className="font-display text-[19px] font-bold tracking-tight">{t('more.title')}</h1>
      <p className="mt-1 text-[12.5px] leading-relaxed text-raad-muted">{t('more.intro')}</p>

      <nav aria-label={t('more.title')} className="mt-4 overflow-hidden rounded-panel border border-raad-border">
        {ROWS.map(({ to, key }, i) => (
          <Link
            key={to}
            to={to}
            className={[
              'flex min-h-14 items-center gap-3 bg-raad-panel px-4 text-sm font-semibold text-raad-text transition-colors hover:bg-raad-input',
              i > 0 ? 'border-t border-raad-border' : '',
            ].join(' ')}
          >
            <span className="flex-1">{t(`nav.${key}`)}</span>
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.8"
              aria-hidden="true"
              className="flex-none text-raad-muted"
            >
              <path d={CHEVRON} />
            </svg>
          </Link>
        ))}
      </nav>
    </section>
  );
}
